import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useLicense } from "../../context/licenseContext";
import apiClient from "../../api/apiClient";
import "./ImportSalesModal.css";

const ImportSalesModal = ({ onClose, onImported }) => {
    const { currentLicense } = useLicense();
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState("idle"); // idle | uploading | success | error
    const [result, setResult] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");

    const onDrop = useCallback((accepted) => {
        if (accepted.length > 0) {
            setFile(accepted[0]);
            setStatus("idle");
            setResult(null);
            setErrorMsg("");
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "text/csv": [".csv"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        },
        multiple: false,
    });

    const handleImport = async () => {
        if (!file || !currentLicense?.licenseNo) return;
        setStatus("uploading");
        setErrorMsg("");

        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("licenseNo", currentLicense.licenseNo);

            const { data } = await apiClient.post("/sales/import", formData);
            setResult(data);
            setStatus("success");
            if (typeof onImported === "function") onImported(data);
        } catch (err) {
            setErrorMsg(err.response?.data?.error || "Import failed. Please try again.");
            setStatus("error");
        }
    };

    const handleReset = () => {
        setFile(null);
        setStatus("idle");
        setResult(null);
        setErrorMsg("");
    };

    const formatBytes = (b) => {
        if (b < 1024) return b + " B";
        if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
        return (b / (1024 * 1024)).toFixed(1) + " MB";
    };

    return (
        <div className="ism-backdrop" onClick={onClose}>
            <div className="ism-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="ism-header">
                    <div className="ism-header-title">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <span>Import Sales Data</span>
                    </div>
                    <button className="ism-close-btn" onClick={onClose} aria-label="Close">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="ism-body">
                    {status !== "success" && (
                        <>
                            <p className="ism-description">
                                Upload a <strong>CSV or Excel</strong> file containing your sales records.
                                Columns are detected automatically — common headers like <em>Date</em>,{" "}
                                <em>Product</em>, <em>Strain</em>, <em>Quantity</em>, <em>Total</em>, and{" "}
                                <em>Customer</em> are recognised.
                            </p>

                            {/* Drop zone */}
                            <div
                                {...getRootProps()}
                                className={`ism-dropzone${isDragActive ? " ism-dropzone--active" : ""}${file ? " ism-dropzone--has-file" : ""}`}
                            >
                                <input {...getInputProps()} />
                                {file ? (
                                    <div className="ism-file-info">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                            <polyline points="14 2 14 8 20 8"/>
                                        </svg>
                                        <div>
                                            <p className="ism-file-name">{file.name}</p>
                                            <p className="ism-file-size">{formatBytes(file.size)}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="ism-dropzone-prompt">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                            <polyline points="17 8 12 3 7 8"/>
                                            <line x1="12" y1="3" x2="12" y2="15"/>
                                        </svg>
                                        <p>{isDragActive ? "Drop your file here" : "Drag & drop a CSV or Excel file, or click to browse"}</p>
                                        <span>.csv, .xls, .xlsx — up to 20 MB</span>
                                    </div>
                                )}
                            </div>

                            {status === "error" && (
                                <p className="ism-error-msg">{errorMsg}</p>
                            )}
                        </>
                    )}

                    {status === "success" && result && (
                        <div className="ism-success">
                            <div className="ism-success-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                    <polyline points="22 4 12 14.01 9 11.01"/>
                                </svg>
                            </div>
                            <h3>Import complete</h3>
                            <p><strong>{result.rowCount.toLocaleString()}</strong> rows imported successfully.</p>
                            <p className="ism-success-sub">Import ID: #{result.importId}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="ism-footer">
                    {status === "success" ? (
                        <>
                            <button className="ism-btn ism-btn--secondary" onClick={handleReset}>
                                Import another file
                            </button>
                            <button className="ism-btn ism-btn--primary" onClick={onClose}>
                                Done
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="ism-btn ism-btn--secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                className="ism-btn ism-btn--primary"
                                onClick={handleImport}
                                disabled={!file || status === "uploading"}
                            >
                                {status === "uploading" ? (
                                    <>
                                        <span className="ism-spinner" />
                                        Importing…
                                    </>
                                ) : (
                                    "Import"
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportSalesModal;
